import { StateService } from "@uirouter/angularjs";
import { IOnInit, IPromise } from "angular";
import { IFieldArray } from "AngularFormly";
import { FormViewController } from "../../../../../../../records/forms/formView/controller";
import { RecordFileTemplateOptions } from "../../../../../../../records/forms/formly/types";
import {
  FormViewService,
  vsName as FormViewServiceName,
} from "../../../../../../../records/forms/formView/service";
import fieldHelpers from "../../../../../../../ui-formly/fieldHelpers";
import { MachineGateway } from "../../../../../models/machine";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../../../../../records/forms/formly/fieldConfigurationHelpers";
import {
  MachineArea,
  MachineBank,
  MachineZone,
} from "../../../../../models/machineConfiguration";
import { Property } from "../../../../../../../property/model/Property";
import { Record } from "../../../../../../../records/server/crud";

export class MachineDetailConfigurationsMoveController implements IOnInit {
  static $inject = [
    FormViewServiceName,
    "$state",
    FieldConfigurationHelperServiceName,
  ];
  readonly formFields: IFieldArray;
  model: any;
  /* start bindings */
  readonly machine!: MachineGateway;
  /* end binding */
  readonly additionalInfo: {
    MachineModelTypeId?: number;
    PropertyId?: number;
    MachineAreaId?: number;
    MachineZoneId?: number;
    MachineManufacturerId?: number;
    MachineBankId?: number;
  };

  constructor(
    private readonly formViewService: FormViewService,
    private readonly $state: StateService,
    private readonly fieldConfigurationHelperService: FieldConfigurationHelperService
  ) {
    const fileTemplateOptions: RecordFileTemplateOptions = {
      allowMultiple: true,
      label: "Files",
    };
    this.additionalInfo = {};

    const that = this;
    this.formFields = [
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleDateField({
            key: "Date",
            name: "Date",
            label: "Project Date",
            required: true,
          }),
          fieldHelpers.simpleStringField({
            key: "ProjectNumber",
            name: "ProjectNumber",
            label: "Project Number",
            required: true,
          }),
        ],
        4
      ),
      ...fieldHelpers.columnLayout(
        [
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "PropertyId",
            name: "PropertyId",
            label: "Property",
            required: true,
            getDefaultItem: () => undefined,
            queryUrl: "/api/properties",
          }),
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "MachineAreaId",
            name: "MachineAreaId",
            label: "Area",
            required: true,
            model: this.additionalInfo,
            getDefaultItem: () => undefined,
            disableDefaultQueryCaching: true,
            queryUrl: "/api/machines/MachineArea",
            queryBuilder: (search) => {
              const propertyId = this.model.PropertyId ?? 0;
              return {
                NameContains: search,
                PropertyId: propertyId,
              };
            },
            watcher: {
              expression: () => {
                return that.model.PropertyId;
              },
              listener: (_, newValue, oldValue) => {
                if (newValue !== oldValue) {
                  that.additionalInfo.MachineAreaId = undefined;
                }
              },
            },
          }),
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "MachineZoneId",
            name: "MachineZoneId",
            label: "Zone",
            required: true,
            model: this.additionalInfo,
            getDefaultItem: () => undefined,
            disableDefaultQueryCaching: true,
            queryUrl: "/api/machines/MachineZone",
            queryBuilder: (search) => {
              const machineAreaId = this.additionalInfo.MachineAreaId ?? 0;
              return {
                NameContains: search,
                MachineAreaId: machineAreaId,
              };
            },
            watcher: {
              expression: () => that.additionalInfo.MachineAreaId,
              listener: (_, newValue, oldValue) => {
                if (newValue !== oldValue) {
                  that.additionalInfo.MachineZoneId = undefined;
                }
              },
            },
          }),
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "MachineBankId",
            name: "MachineBankId",
            label: "Bank",
            required: true,
            getDefaultItem: () => undefined,
            queryBuilder: (search) => {
              const machineZoneId = that.additionalInfo.MachineZoneId ?? 0;
              return {
                NameContains: search,
                MachineZoneId: machineZoneId,
              };
            },
            watcher: {
              expression: () => that.additionalInfo.MachineZoneId,
              listener: (_, newValue, oldValue) => {
                if (newValue !== oldValue) {
                  that.additionalInfo.MachineBankId = undefined;
                }
              },
            },
            queryUrl: "/api/machines/MachineBank",
            disableDefaultQueryCaching: true,
          }),
          fieldHelpers.simpleStringField({
            key: "Position",
            name: "Position",
            label: "Position",
            required: true,
          }),
        ],
        4
      ),
      {
        key: "Files",
        name: "Files",
        type: "recordFileUpload",
        templateOptions: fileTemplateOptions,
      },
      fieldHelpers.headerField({
        headerText: "Notes",
        key: "NotesHeader",
      }),
      fieldHelpers.textAreaField({
        key: "Notes",
        name: "Notes",
        label: " ",
        rows: 5,
      }),
    ];
  }

  $onInit(): void {
    const currentConfiguration = this.machine.Configs?.find(
      (c) => c.CurrentConfiguration
    );
    this.model = {
      MachineId: this.machine.Id,
      Files: currentConfiguration?.Files?.map((f: any) => {
        return { Name: f.Name, ContentType: f.ContentType, Url: f.Url };
      }),
    };
  }

  async submit(formViewController: FormViewController): Promise<void> {
    const machineId = this.machine.Id;
    await this.formViewService.submitForm({
      url: `/api/machines/${machineId}/move`,
      formObject: this.model,
      formViewController,
      method: "post",
    });

    await this.$state.go("^", undefined, { reload: true });
  }

  async cancel(): Promise<void> {
    await this.$state.go("^");
  }
  private getProperty(): Property | undefined {
    return this.getMachineArea()?.Property;
  }

  private getMachineArea(): MachineArea | undefined {
    return this.getMachineZone()?.MachineArea;
  }

  private getMachineZone(): MachineZone | undefined {
    return this.getMachineBank()?.MachineZone;
  }

  private getMachineBank(): MachineBank | undefined {
    return undefined;
  }
}
